import { useState } from 'react';
import type { Decision, PlaybookParams } from '@reelmachine/core';
import { useDecide, useDecisions, useIsAdmin, useLearningOverview } from '../../api/learning';
import { describePatch } from '../../lib/learning-format';
import { formatWhen } from '../../lib/format';
import { renderInline } from '../../lib/markdown';
import { useToast } from '../../ui/Toast';
import { QueryState } from './QueryState';

const KIND_LABEL: Record<Decision['kind'], string> = {
  playbook_change: 'Playbook change',
  experiment: 'Experiment',
  code_change: 'Code change',
};

const STATUS_CHIP: Record<Decision['status'], string> = {
  open: '',
  approved: 'ok',
  applied: 'ok',
  rejected: 'warn',
  expired: '',
};

export function PatchDiff({ current, patch }: { current: PlaybookParams | undefined; patch: Decision['patch'] }) {
  if (!patch) return null;
  const changes = describePatch(current, patch);
  if (!changes.length) return <p className="muted small">No difference to the active playbook.</p>;
  return (
    <div className="diff" aria-label="Changes">
      {changes.map((c) => (
        <div key={c.field}>
          <span className="field-l">{c.label}</span>
          <ul>
            {c.lines.map((l, i) => (
              <li key={i} className={`diff-${l.kind}`}>
                <span aria-hidden="true">{l.kind === 'add' ? '+' : l.kind === 'remove' ? '−' : '~'}</span>
                <span className="visually-hidden">
                  {l.kind === 'add' ? 'Added' : l.kind === 'remove' ? 'Removed' : 'Changed'}:{' '}
                </span>
                {l.text}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function DecisionCard({ d, current, admin }: { d: Decision; current: PlaybookParams | undefined; admin: boolean }) {
  const toast = useToast();
  const decide = useDecide();
  const [note, setNote] = useState('');
  const open = d.status === 'open';
  const run = (decision: 'approve' | 'reject') =>
    decide.mutate(
      { decisionId: d.id, decision, ...(note.trim() ? { note: note.trim() } : {}) },
      {
        onSuccess: () => toast(decision === 'approve' ? 'Approved' : 'Rejected'),
        onError: (e) => toast(e.message),
      },
    );
  return (
    <article className="card" aria-label={d.title}>
      <div className="row" style={{ gap: 6 }}>
        <span className="chip">{KIND_LABEL[d.kind]}</span>
        {!open && (
          <span className={`chip ${STATUS_CHIP[d.status]}`}>{d.status[0]!.toUpperCase() + d.status.slice(1)}</span>
        )}
      </div>
      <h3>{d.title}</h3>
      <p>{renderInline(d.rationale)}</p>
      {d.evidence && (
        <details>
          <summary className="label">Evidence</summary>
          <p className="small">{renderInline(d.evidence)}</p>
        </details>
      )}
      {open && <PatchDiff current={current} patch={d.patch} />}
      {d.link && <p className="small">{renderInline(`[Open the change](${d.link})`)}</p>}
      {!open && d.decidedAt && (
        <p className="nums">
          {d.decidedBy ? `${d.decidedBy}, ` : ''}
          {formatWhen(d.decidedAt)}
        </p>
      )}
      {open && admin && (
        <>
          <div className="field">
            <label className="field-l" htmlFor={`note-${d.id}`}>
              Note (optional)
            </label>
            <input
              id={`note-${d.id}`}
              type="text"
              maxLength={1000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
          <div className="row nowrap">
            <button type="button" className="btn sec" disabled={decide.isPending} onClick={() => run('reject')}>
              Reject
            </button>
            <button type="button" className="btn" disabled={decide.isPending} onClick={() => run('approve')}>
              Approve
            </button>
          </div>
        </>
      )}
    </article>
  );
}

export function DecisionsTab() {
  const q = useDecisions();
  const overview = useLearningOverview();
  const admin = useIsAdmin();
  const current = overview.data?.activeVersion?.params;
  return (
    <QueryState query={q}>
      {(decisions) => {
        const open = decisions.filter((d) => d.status === 'open');
        const done = decisions.filter((d) => d.status !== 'open');
        return (
          <>
            <span className="label">Open ({open.length})</span>
            {open.length === 0 && <p className="muted">Nothing to decide right now.</p>}
            {open.map((d) => (
              <DecisionCard key={d.id} d={d} current={current} admin={admin} />
            ))}
            {done.length > 0 && <span className="label">Decided</span>}
            {done.map((d) => (
              <DecisionCard key={d.id} d={d} current={current} admin={false} />
            ))}
          </>
        );
      }}
    </QueryState>
  );
}
